"use client";
import { useEffect, useState } from "react";

interface CountdownProps {
  targetDate: Date;
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "brand" | "onPink";
}

export default function Countdown({ targetDate, className = "", size = "md", variant = "brand" }: CountdownProps) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = targetDate.getTime() - new Date().getTime();
      
      if (difference <= 0) {
        // Si ya pasó la fecha objetivo
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      });
    };

    // Calcular inmediatamente
    calculateTimeLeft();
    
    // Actualizar cada segundo
    const timer = setInterval(calculateTimeLeft, 1000);
    
    // Limpiar intervalo al desmontar
    return () => clearInterval(timer);
  }, [targetDate]);

  const sizeCfg = {
    sm: {
      gap: "gap-1 md:gap-2",
      boxPad: "p-1.5 md:p-2",
      boxWidth: "min-w-[42px] md:min-w-[56px]",
      numberText: "text-base md:text-xl",
      colonText: "text-lg md:text-2xl",
    },
    md: {
      gap: "gap-2 md:gap-4",
      boxPad: "p-2 md:p-3",
      boxWidth: "min-w-[50px] md:min-w-[70px]",
      numberText: "text-xl md:text-3xl",
      colonText: "text-xl md:text-3xl",
    },
    lg: {
      gap: "gap-3 md:gap-6",
      boxPad: "p-3 md:p-4",
      boxWidth: "min-w-[60px] md:min-w-[80px]",
      numberText: "text-2xl md:text-4xl",
      colonText: "text-2xl md:text-4xl",
    },
  } as const;

  const cfg = sizeCfg[size] ?? sizeCfg.md;

  const colorCfg = {
    brand: {
      box: "bg-[#7d4168] text-white",
      colon: "text-[#7d4168]",
      label: "text-[#7d4168]",
    },
    onPink: {
      box: "bg-white/20 border border-white/30 text-white",
      colon: "text-white",
      label: "text-white/90",
    },
  } as const;
  const colors = colorCfg[variant] ?? colorCfg.brand;

  return (
    <div className={`flex items-center justify-center ${cfg.gap} ${className}`}>
      <div className="flex flex-col items-center">
        <div className={`${colors.box} rounded-lg ${cfg.boxPad} ${cfg.boxWidth} text-center font-bold ${cfg.numberText}`}>
          {String(timeLeft.days).padStart(2, '0')}
        </div>
        <span className={`text-xs md:text-sm mt-1 font-medium ${colors.label}`}>Días</span>
      </div>
      <span className={`${colors.colon} ${cfg.colonText} font-bold`}>:</span>
      <div className="flex flex-col items-center">
        <div className={`${colors.box} rounded-lg ${cfg.boxPad} ${cfg.boxWidth} text-center font-bold ${cfg.numberText}`}>
          {String(timeLeft.hours).padStart(2, '0')}
        </div>
        <span className={`text-xs md:text-sm mt-1 font-medium ${colors.label}`}>Horas</span>
      </div>
      <span className={`${colors.colon} ${cfg.colonText} font-bold`}>:</span>
      <div className="flex flex-col items-center">
        <div className={`${colors.box} rounded-lg ${cfg.boxPad} ${cfg.boxWidth} text-center font-bold ${cfg.numberText}`}>
          {String(timeLeft.minutes).padStart(2, '0')}
        </div>
        <span className={`text-xs md:text-sm mt-1 font-medium ${colors.label}`}>Minutos</span>
      </div>
      <span className={`${colors.colon} ${cfg.colonText} font-bold`}>:</span>
      <div className="flex flex-col items-center">
        <div className={`${colors.box} rounded-lg ${cfg.boxPad} ${cfg.boxWidth} text-center font-bold ${cfg.numberText}`}>
          {String(timeLeft.seconds).padStart(2, '0')}
        </div>
        <span className={`text-xs md:text-sm mt-1 font-medium ${colors.label}`}>Segundos</span>
      </div>
    </div>
  );
}